import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Star, Award, CheckCircle, Phone, Calendar, MapPin, BadgeCheck, Shield, DollarSign, Clock, Zap, Heart } from 'lucide-react';
import AppointmentForm from '@/components/forms/AppointmentForm';
import MonthlyPaymentMatcher from '@/components/forms/MonthlyPaymentMatcher';

const DentalImplantsFortWorth = () => {
  // Schema for Dental Implants page - optimized for local SEO
  const dentalImplantSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalProcedure",
    "@id": "https://fortworthdental.com/dental-implants-fort-worth#procedure",
    "name": "Dental Implants Fort Worth",
    "alternateName": ["Dental Implants in Fort Worth", "Fort Worth Dental Implants", "Implant Dentist Fort Worth"],
    "description": "Fort Worth Dental offers affordable dental implants in Fort Worth, TX. Dr. Arthur Mund is a Board Certified Implant Specialist (Diplomate ABOI) with 30+ years experience. FREE implant consultation including X-rays.",
    "url": "https://fortworthdental.com/dental-implants-fort-worth",
    "procedureType": "Surgical",
    "bodyLocation": "Mouth",
    "preparation": "Consultation and X-rays",
    "followup": "Regular checkups",
    "howPerformed": "Titanium implant placed into jawbone, followed by crown attachment after healing",
    "status": "EventScheduled"
  };

  const dentistSchema = {
    "@context": "https://schema.org",
    "@type": "Dentist",
    "name": "Fort Worth Dental - Implant Center",
    "description": "Board certified dental implant specialists serving Fort Worth, TX since 1990. Dr. Arthur Mund, Diplomate of the American Board of Oral Implantology.",
    "url": "https://fortworthdental.com/dental-implants-fort-worth",
    "telephone": "(817) 985-3337",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "4620 Citylake Blvd W",
      "addressLocality": "Fort Worth",
      "addressRegion": "TX",
      "postalCode": "76132",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 32.6829,
      "longitude": -97.4089
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "1110",
      "bestRating": "5"
    },
    "priceRange": "$$",
    "medicalSpecialty": "Implant Dentistry",
    "availableService": {
      "@type": "MedicalProcedure",
      "name": "Dental Implants",
      "description": "Permanent tooth replacement using titanium implants"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How much do dental implants cost in Fort Worth?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Dental implant cost depends on the number of implants, bone grafting needs, and the type of restoration — every case is unique. At Fort Worth Dental we provide a personalized written quote at your FREE consultation (including X-rays, a $475 value), so you know exactly what your treatment will cost before anything begins."
        }
      },
      {
        "@type": "Question",
        "name": "Who is the best dental implant dentist in Fort Worth?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Dr. Arthur Mund at Fort Worth Dental is one of the most qualified implant dentists in Fort Worth. He is a Diplomate of the American Board of Oral Implantology (ABOI)—one of only a few hundred dentists nationwide with this prestigious certification. With 30+ years of implant experience and 1,100+ 5-star reviews, Fort Worth Dental is consistently rated among the best."
        }
      },
      {
        "@type": "Question",
        "name": "Are dental implants worth it?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, dental implants are considered the gold standard for tooth replacement. Unlike dentures, implants look, feel, and function like natural teeth. They preserve jawbone, prevent facial sagging, and can last a lifetime with proper care. Most patients say dental implants significantly improve their quality of life."
        }
      },
      {
        "@type": "Question",
        "name": "Does Fort Worth Dental offer affordable dental implants?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes! Fort Worth Dental offers flexible financing options for dental implants including CareCredit and in-house payment plans. We also accept most dental insurance plans and offer a FREE implant consultation including X-rays (a $475 value)."
        }
      },
      {
        "@type": "Question",
        "name": "How long do dental implants take to heal?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The complete dental implant process typically takes 3-6 months. After the implant is placed, it needs 2-4 months to fuse with your jawbone (osseointegration). Then the crown is attached. Some patients qualify for same-day implants where a temporary crown is placed immediately."
        }
      }
    ]
  };

  const implantBenefits = [
    { title: "Look & Feel Natural", description: "Implants function exactly like your natural teeth", icon: Star },
    { title: "Lifetime Solution", description: "With proper care, implants can last 25+ years", icon: Shield },
    { title: "Preserve Jawbone", description: "Prevent bone loss and facial changes", icon: Heart },
    { title: "No Dietary Restrictions", description: "Eat all your favorite foods confidently", icon: CheckCircle },
    { title: "Easy Maintenance", description: "Brush and floss just like natural teeth", icon: Zap },
    { title: "Boost Confidence", description: "Smile, speak, and laugh without worry", icon: Award }
  ];

  const whyChooseUs = [
    {
      title: "Board Certified Specialist",
      description: "Dr. Mund is a Diplomate of the American Board of Oral Implantology—among the highest credentials in implant dentistry.",
      icon: BadgeCheck
    },
    {
      title: "30+ Years Experience",
      description: "Thousands of successful implant procedures since 1990. We've seen every case imaginable.",
      icon: Clock
    },
    {
      title: "1,100+ 5-Star Reviews",
      description: "Our patients consistently rate us among the best dental implant providers in Fort Worth.",
      icon: Star
    },
    {
      title: "FREE Consultation",
      description: "Get a complete evaluation including X-rays ($475 value) at no cost. Know your options before committing.",
      icon: DollarSign
    },
    {
      title: "Affordable Financing",
      description: "CareCredit, in-house plans, and insurance accepted. Quality implants shouldn't break the bank.",
      icon: Shield
    },
    {
      title: "All-In-One Practice",
      description: "Consultation, surgery, and crown placement—all under one roof. No referrals needed.",
      icon: MapPin
    }
  ];

  const implantTypes = [
    {
      name: "Single Tooth Implant",
      description: "Replace one missing tooth with a permanent, natural-looking solution.",
      price: "Personalized Quote",
      features: ["Titanium post", "Custom ceramic crown", "Looks like natural tooth"]
    },
    {
      name: "Implant Bridge",
      description: "Replace 3+ adjacent missing teeth using 2 implants.",
      price: "Personalized Quote",
      features: ["2 implant posts", "3-4 tooth bridge", "No damage to healthy teeth"]
    },
    {
      name: "All-on-4® Implants",
      description: "Full arch replacement with just 4 strategically placed implants.",
      price: "Personalized Quote",
      features: ["Full arch restoration", "Same-day teeth possible", "No bone grafting usually needed"]
    },
    {
      name: "Mini Dental Implants",
      description: "Smaller implants ideal for denture stabilization or limited bone.",
      price: "Personalized Quote",
      features: ["Less invasive", "Faster healing", "Great for dentures"]
    }
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Dental Implants Fort Worth | FREE Consultation | Board Certified Specialist</title>
        <meta name="description" content="Looking for dental implants in Fort Worth? Dr. Mund is a Board Certified Implant Specialist with 30+ years experience. FREE consultation + X-rays ($475 value). 1,100+ 5-star reviews. Call (817) 985-3337." />
        <meta name="keywords" content="dental implants fort worth, dental implants fort worth tx, fort worth dental implants, dental implant fort worth, implant dentist fort worth, affordable dental implants fort worth, best dental implants fort worth" />
        <link rel="canonical" href="https://fortworthdental.com/dental-implants-fort-worth" />
        <meta property="og:title" content="Dental Implants Fort Worth | Board Certified Implant Specialist" />
        <meta property="og:description" content="Fort Worth's trusted dental implant center. Dr. Mund is a Diplomate of ABOI with 30+ years experience. FREE consultation including X-rays. Financing available." />
        <meta property="og:url" content="https://fortworthdental.com/dental-implants-fort-worth" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(dentalImplantSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(dentistSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-700 via-teal-600 to-teal-800 text-white py-16 lg:py-20">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex flex-wrap gap-3 mb-6">
                <span className="inline-flex items-center gap-1 bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-sm font-semibold">
                  <Star className="w-4 h-4 fill-current" /> Board Certified Specialist
                </span>
                <span className="inline-flex items-center gap-1 bg-white/20 text-white px-3 py-1 rounded-full text-sm font-medium">
                  1,100+ 5-Star Reviews
                </span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Dental Implants in <span className="text-yellow-300">Fort Worth</span>
              </h1>
              
              <p className="text-lg lg:text-xl text-teal-50 mb-8 leading-relaxed">
                Restore your smile with Fort Worth&apos;s most trusted implant dentist. Dr. Mund is a 
                <strong> Diplomate of the American Board of Oral Implantology</strong>—one of only 
                a few hundred dentists nationwide with this elite certification.
              </p>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-8">
                <p className="text-yellow-300 font-semibold text-lg mb-1">🎉 FREE Implant Consultation</p>
                <p className="text-teal-100 text-sm">Includes comprehensive exam & X-rays (a $475 value)</p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <a href="tel:8179853337" className="flex items-center justify-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-yellow-900 px-6 py-4 rounded-lg font-semibold transition-all text-lg">
                  <Phone className="w-5 h-5" />
                  Call (817) 985-3337
                </a>
                <Link to="/contact" className="flex items-center justify-center gap-2 bg-white hover:bg-gray-100 text-teal-700 px-6 py-4 rounded-lg font-semibold transition-all border-2 border-white">
                  <Calendar className="w-5 h-5" />
                  Book FREE Consultation
                </Link>
              </div>

              <div className="flex items-center gap-2 mt-6 text-teal-100">
                <MapPin className="w-4 h-4" />
                <span className="text-sm">4620 Citylake Blvd W, Fort Worth, TX 76132</span>
              </div>
            </div>

            <div className="relative">
              <img 
                src="https://fortworthdental.com/images/team.png" 
                alt="Fort Worth Dental implant team with Dr. Mund"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="bg-teal-100 rounded-full p-2">
                    <Award className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">Best of Fort Worth</p>
                    <p className="text-sm text-gray-600">2025 Winner</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="bg-gray-50 py-8 border-b">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div className="flex flex-col items-center">
              <BadgeCheck className="w-8 h-8 text-teal-600 mb-2" />
              <p className="font-bold text-gray-900">Diplomate ABOI</p>
              <p className="text-sm text-gray-600">Board Certified</p>
            </div>
            <div className="flex flex-col items-center">
              <Star className="w-8 h-8 text-yellow-500 mb-2" />
              <p className="font-bold text-gray-900">1,100+ Reviews</p>
              <p className="text-sm text-gray-600">4.9 Star Average</p>
            </div>
            <div className="flex flex-col items-center">
              <Clock className="w-8 h-8 text-teal-600 mb-2" />
              <p className="font-bold text-gray-900">30+ Years</p>
              <p className="text-sm text-gray-600">Implant Experience</p>
            </div>
            <div className="flex flex-col items-center">
              <Shield className="w-8 h-8 text-teal-600 mb-2" />
              <p className="font-bold text-gray-900">Warranty</p>
              <p className="text-sm text-gray-600">On All Implants</p>
            </div>
          </div>
        </div>
      </section>

      {/* What Are Dental Implants */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              What Are Dental Implants?
            </h2>
            <p className="text-lg text-gray-600">
              Dental implants are the gold standard for replacing missing teeth. A small titanium post 
              is placed in your jawbone, where it fuses naturally with the bone. Then a custom-made 
              crown is attached, giving you a permanent tooth that looks, feels, and functions just 
              like your natural teeth.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {implantBenefits.map((benefit, index) => (
              <div key={index} className="flex items-start gap-4 p-6 bg-gray-50 rounded-xl">
                <div className="bg-teal-100 rounded-lg p-3 flex-shrink-0">
                  <benefit.icon className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Why Choose Fort Worth Dental for Implants?
            </h2>
            <p className="text-gray-300 text-lg max-w-2xl mx-auto">
              Not all implant dentists are created equal. Here&apos;s what sets us apart from other 
              Fort Worth dental implant providers.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <div className="bg-teal-500/20 rounded-lg p-3 w-fit mb-4">
                  <item.icon className="w-6 h-6 text-teal-400" />
                </div>
                <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                <p className="text-gray-300">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Implant Types & Pricing */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Dental Implant Options & Pricing
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              We offer multiple implant solutions to fit your needs and budget. 
              All pricing includes your FREE consultation.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {implantTypes.map((type, index) => (
              <div key={index} className="border-2 border-gray-200 rounded-xl p-6 hover:border-teal-500 transition-colors">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{type.name}</h3>
                <p className="text-gray-600 mb-4">{type.description}</p>
                <p className="text-2xl font-bold text-teal-600 mb-4">{type.price}</p>
                <ul className="space-y-2">
                  {type.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-gray-700">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <p className="text-gray-600 mb-4">
              * Prices may vary based on individual treatment needs. Financing available.
            </p>
            <Link to="/financing" className="text-teal-600 hover:text-teal-700 font-semibold underline">
              View Financing Options →
            </Link>
          </div>
        </div>
      </section>

      {/* Dr. Mund Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <img 
                  src="/images/team/dr-mund.jpg" 
                  alt="Dr. Arthur Mund - Board Certified Implant Dentist Fort Worth"
                  className="rounded-xl shadow-lg"
                />
              </div>
              <div>
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                  Meet Dr. Arthur Mund
                </h2>
                <p className="text-lg text-gray-600 mb-4">
                  <strong>Diplomate, American Board of Oral Implantology</strong>
                </p>
                <p className="text-gray-600 mb-4">
                  Dr. Mund has dedicated his career to dental implant excellence. As one of 
                  approximately 500 dentists nationwide who have achieved Diplomate status 
                  with the ABOI, he brings unparalleled expertise to every implant case.
                </p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                    <span>30+ years placing dental implants</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                    <span>Top Dentist - Fort Worth Magazine (13x winner)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                    <span>Fellow, American Academy of Implant Dentistry</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                    <span>Thousands of successful implants placed</span>
                  </li>
                </ul>
                <Link to="/team" className="text-teal-600 hover:text-teal-700 font-semibold">
                  Meet Our Full Team →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-8 text-center">
              Dental Implant FAQs
            </h2>
            
            <div className="space-y-6">
              {faqSchema.mainEntity.map((faq, index) => (
                <div key={index} className="border-b border-gray-200 pb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{faq.name}</h3>
                  <p className="text-gray-600">{faq.acceptedAnswer.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-teal-600 to-teal-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Ready to Restore Your Smile?
            </h2>
            <p className="text-xl text-teal-100 mb-8">
              Schedule your FREE dental implant consultation today. 
              Includes comprehensive exam and X-rays (a $475 value).
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:8179853337" className="flex items-center justify-center gap-2 bg-white hover:bg-gray-100 text-teal-700 px-8 py-4 rounded-lg font-semibold transition-all text-lg">
                <Phone className="w-5 h-5" />
                Call (817) 985-3337
              </a>
              <Link to="/contact" className="flex items-center justify-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-yellow-900 px-8 py-4 rounded-lg font-semibold transition-all">
                <Calendar className="w-5 h-5" />
                Book Online Now
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Monthly Payment Matcher */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 max-w-2xl">
          <MonthlyPaymentMatcher />
        </div>
      </section>

      {/* Appointment Form Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              Request Your FREE Implant Consultation
            </h2>
            <AppointmentForm />
          </div>
        </div>
      </section>
    </div>
  );
};

export default DentalImplantsFortWorth;
